interface WordGroup {
    color: string;
    words: string[];
}

interface Range {
    color: string;
    class?: string;
    start: number;
    end: number;
}

export interface HighlighterOptions {
    words: Record<string, string[]>;
    ranges: Range[] | number[];
    color: string;
    caseSensitive: boolean;
    wordsOnly: boolean;
    resizable: boolean;
    resizableOptions: Record<string, any>;
    id: string;
    debug: boolean;
}

class Utilities {
    static getScrollbarWidth(): number {
        const parent = document.createElement('div');
        parent.style.width = '50px';
        parent.style.height = '50px';
        parent.style.overflow = 'auto';
        parent.innerHTML = '<div></div>';
        document.body.appendChild(parent);

        const child = parent.children[0] as HTMLElement;
        const width = child.offsetWidth - child.clientWidth;

        document.body.removeChild(parent);

        return width;
    }

    static cloneCss(from: HTMLElement, to: HTMLElement, properties: string[]): void {
        properties.forEach((property: any) => {
            to.style[property] = getComputedStyle(from).getPropertyValue(property);
        });
    }

    static toPx(value: string): number {
        if (value.includes('em')) {
            const el = document.createElement('div');
            el.style.fontSize = '1em';
            el.style.margin = '0';
            el.style.padding = '0';
            el.style.height = 'auto';
            el.style.lineHeight = '1';
            el.style.border = '0';
            el.innerHTML = '&nbsp;';
            document.body.appendChild(el);

            const height = el.offsetHeight;
            document.body.removeChild(el);

            return Math.round(parseFloat(value.replace('em', '')) * height);
        } else if (value.includes('px')) {
            return parseInt(value.replace('px', ''), 10);
        } else {
            return parseInt(value, 10);
        }
    }

    static htmlEntities(str: string): string {
        const tempEl = document.createElement('div');
        tempEl.textContent = str;
        return tempEl.innerHTML;
    }

    static strInsert(string: string, index: number, value: string): string {
        return string.slice(0, index) + value + string.slice(index);
    }

    static throttle(callback: () => void, delay: number, context: any): () => void {
        let last = 0;
        let pid: number | null = null;

        return function (this: any) {
            const now = new Date().getTime();
            const args = arguments as IArguments;
            const that = this;

            function exec() {
                last = new Date().getTime();

                if (context) {
                    return callback.apply(context, Array.prototype.slice.call(args) as any);
                } else {
                    return callback.apply(that, Array.prototype.slice.call(args) as any);
                }
            }

            if (now - last > delay) {
                return exec();
            } else {
                clearTimeout(pid as number);
                pid = window.setTimeout(exec, delay - (now - last));
            }
        };
    }

    static cleanWords(words: any, color: string): Record<string, string[]> {
        const out: Record<string, string[]> = {};

        if (!Array.isArray(words)) {
            words = [words];
        }

        for (let i = 0, l = words.length; i < l; i++) {
            const group = words[i];

            if (typeof group === 'object' && group !== null) {
                if (!out[group.color]) {
                    out[group.color] = [];
                }

                if (!Array.isArray(group.words)) {
                    group.words = [group.words];
                }

                for (let j = 0, m = group.words.length; j < m; j++) {
                    out[group.color].push(Utilities.htmlEntities(group.words[j]));
                }
            } else {
                if (!out[color]) {
                    out[color] = [];
                }

                out[color].push(Utilities.htmlEntities(group));
            }
        }

        return out;
    }

    static cleanRanges(ranges: any, color: string): Range[] {
        const out: any[] = [];

        if (
            (typeof ranges === 'object' && ranges !== null && !Array.isArray(ranges[0])) ||
            (Utilities.isNumeric(ranges[0]) && Utilities.isNumeric(ranges[1]))
        ) {
            ranges = [ranges];
        }

        for (let i = 0, l = ranges.length; i < l; i++) {
            const range = ranges[i];

            if (Array.isArray(range)) {
                out.push({
                    color: color,
                    start: range[0],
                    end: range[1]
                });
            } else {
                if (range.ranges) {
                    if (typeof range.ranges === 'object' && range.ranges !== null && !Array.isArray(range.ranges[0])) {
                        range.ranges = [range.ranges];
                    }

                    for (let j = 0, m = range.ranges.length; j < m; j++) {
                        if (Array.isArray(range.ranges[j])) {
                            out.push({
                                color: range.color,
                                class: range.class,
                                start: range.ranges[j][0],
                                end: range.ranges[j][1]
                            });
                        } else {
                            if (range.ranges[j].length) {
                                range.ranges[j].end = range.ranges[j].start + range.ranges[j].length;
                            }
                            out.push(range.ranges[j]);
                        }
                    }
                } else {
                    if (range.length) {
                        range.end = range.start + range.length;
                    }
                    out.push(range);
                }
            }
        }

        out.sort((a, b) => {
            if (a.start == b.start) {
                return a.end - b.end;
            }
            return a.start - b.start;
        });

        let current = -1;
        out.forEach(range => {
            if (range.start >= range.end) {
                console.error('Invalid range end/start');
            }
            if (range.start < current) {
                console.error('Ranges overlap');
            }
            current = range.end;
        });

        out.reverse();

        return out;
    }

    static isNumeric(n: any): boolean {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }
}

export class Highlighter {
    private settings: HighlighterOptions;
    private scrollbarWidth: number;
    private isInput: boolean;
    private active: boolean;
    private matches: string[];
    private $el: HTMLTextAreaElement | any;
    private $main: HTMLElement;
    private $container: HTMLElement;
    private $highlighter: HTMLElement;
    private regParam!: string;
    private frontSpacer!: string;
    private backSpacer!: string;

    constructor($el: HTMLTextAreaElement, options: HighlighterOptions) {
        this.settings = Object.assign({}, Highlighter.DEFAULTS);
        this.scrollbarWidth = Utilities.getScrollbarWidth();
        this.isInput = $el.tagName.toLowerCase() === 'input';
        this.active = false;
        this.matches = [];

        this.$el = $el;

        // create wrapper container
        const wrapper = document.createElement('div');
        wrapper.className = 'highlightTextarea';

        // insert wrapper before el in the DOM tree
        this.$el.parentNode.insertBefore(wrapper, this.$el);

        // move el into wrapper
        wrapper.appendChild(this.$el);
        
        this.$main = this.$el.parentElement as HTMLElement;

        const highlightTextareaHighlighterDiv = document.createElement('div');
        highlightTextareaHighlighterDiv.className = 'highlightTextarea-highlighter';
        const highlightTextareaContainerDiv = document.createElement('div');
        highlightTextareaContainerDiv.className = 'highlightTextarea-container';
        highlightTextareaContainerDiv.append(highlightTextareaHighlighterDiv);

        this.$main.prepend(highlightTextareaContainerDiv);

        this.$container = this.$main.children[0] as HTMLElement;
        this.$highlighter = this.$container.children[0] as HTMLElement;

        this.setOptions(options);

        if (this.settings.id) {
            this.$main.id = this.settings.id;
        }

        if (this.settings.resizable) {
            this.applyResizable();
        }

        this.updateCss();
        this.bindDocumentEvents();
        this.bindEvents();
        this.highlight();
    }

    static DEFAULTS: HighlighterOptions = {
        words: {},
        ranges: {} as any,
        color: '#ffff00',
        caseSensitive: true,
        wordsOnly: false,
        resizable: false,
        resizableOptions: {},
        id: '',
        debug: false
    };

    highlight(): void {
        let text = this.$el.value;
        const that = this;

        this.frontSpacer = '';
        this.backSpacer = '';

        if (this.settings.wordsOnly) {
            this.frontSpacer = "(^|\\W)";
            this.backSpacer = "(?=$|\\W)";
        }

        const htmlDecode = (input: string): string => {
            const tempEl = document.createElement('div');
            tempEl.innerHTML = input;
            return tempEl.textContent || tempEl.innerText || '';
        }

        text = htmlDecode(text);

        let replaceText = text;
        let colorText = text;

        const matches: string[] = [];
        const matchedIntents: { word: string; color: string }[] = [];

        Object.entries(this.settings.words).forEach(([color, words]) => {
            words.forEach(word => {
                matches.push(word);

                const re = `${that.frontSpacer}(${word})${that.backSpacer}`;
                const regex = new RegExp(re, that.regParam);
                const wordMatches = text.match(regex);

                if (wordMatches) {
                    wordMatches.forEach((match: any) => {
                        if (!matchedIntents.some(intent => intent.word === match)) {
                            matchedIntents.push({ word: match, color: color });
                        }
                    });
                }
            });
        });

        matchedIntents.sort((a, b) => b.word.length - a.word.length);

        matchedIntents.forEach(matchObj => {
            const word = matchObj.word;
            const color = matchObj.color;

            if (replaceText.includes(word)) {
                replaceText = replaceText.replace(word, ' || ');

                colorText = colorText.replace(new RegExp(word, 'g'), (innerMatch: any, start: any, contents: any) => {

                    const charMappings: { [key: string]: string } = {
                        '&': "&amp;",
                        '"': "\"",
                        '<': "&lt;",
                        '>': "&gt;"
                    };
                    
                    let encodedMatch = innerMatch.replace(/[&"<>]/g, (c: string) => {
                        return charMappings[c];
                    });

                    let startString = "";
                    let endString = "";

                    if (/^[ .,!|':;?^$]{1}/.test(encodedMatch)) {
                        startString = encodedMatch[0];
                        encodedMatch = encodedMatch.substr(1);
                    }

                    if (/[ .,!|':;?^$]{1}$/.test(encodedMatch)) {
                        endString = encodedMatch[encodedMatch.length - 1];
                        encodedMatch = encodedMatch.slice(0, -1);
                    }

                    encodedMatch = encodedMatch.replace(/&amp;gt;/g, '>').replace(/&amp;lt;/g, '<');

                    return startString + '<mark style="background-color:' + color + ';">' + encodedMatch.trim() + '</mark>' + endString;
                });
            }
        });

        text = colorText;

        this.settings.ranges.forEach((range: any) => {
            if (range.start < text.length) {
                text = Utilities.strInsert(text, range.end, '</mark>');

                const mark = `<mark style="background-color:${range.color};"`;
                if (range.class !== undefined) {
                    text = Utilities.strInsert(text, range.start, `${mark} class="${range.class}">`);
                } else {
                    text = Utilities.strInsert(text, range.start, `${mark}>`);
                }
            }
        });

        if (matches.length !== this.matches.length) {
            this.matches = matches;
            const matchesChangedEvent = new Event('matchesChanged');
            (matchesChangedEvent as any).matches = this.matches;
            this.$el.dispatchEvent(matchesChangedEvent);
        }

        this.$highlighter.innerHTML = text;
        this.updateSizePosition();
    }

    setWords(words: Record<string, string[]>): void {
        this.setOptions({ words: words, ranges: [] } as any);
    }

    setRanges(ranges: Range[]): void {
        this.setOptions({ words: {}, ranges: ranges } as any);
    }

    enable(): void {
        this.bindEvents();
        this.highlight();
    }

    disable(): void {
        this.unbindEvents();
        this.$highlighter.innerHTML = '';
    }

    destroy(): void {
        this.disable();

        Utilities.cloneCss(this.$container, this.$el, [
            'background-image', 'background-color', 'background-position',
            'background-size', 'background-repeat', 'background-attachment'
        ]);

        Utilities.cloneCss(this.$highlighter, this.$el, [
            'margin-top', 'margin-left', 'margin-right', 'margin-bottom', 'padding-top', 'padding-left',
            'padding-right', 'padding-bottom'
        ]);

        this.$main.parentNode!.replaceChild(this.$el, this.$main);
    }

    setOptions(options: HighlighterOptions): void {
        Object.assign(this.settings, options);
        this.regParam = this.settings.caseSensitive ? 'g' : 'ig';
        this.highlight();
    }

    updateCss(): void {
        const $h = this.$highlighter;

        if (this.isInput) {
            this.$container.style.paddingTop = window.getComputedStyle(this.$el).paddingTop;
            this.$container.style.paddingRight = window.getComputedStyle(this.$el).paddingRight;
            this.$container.style.paddingBottom = window.getComputedStyle(this.$el).paddingBottom;
            this.$container.style.paddingLeft = window.getComputedStyle(this.$el).paddingLeft;
            this.$container.style.borderTop = window.getComputedStyle(this.$el).borderTop;
            this.$container.style.borderRight = window.getComputedStyle(this.$el).borderRight;
            this.$container.style.borderBottom = window.getComputedStyle(this.$el).borderBottom;
            this.$container.style.borderLeft = window.getComputedStyle(this.$el).borderLeft;

            $h.style.backgroundColor = this.settings.color;
            $h.style.color = window.getComputedStyle(this.$el).color;

            this.$highlighter.style.whiteSpace = 'pre-wrap';
        } else {
            this.$container.style.margin = window.getComputedStyle(this.$el).margin;
            this.$container.style.width = window.getComputedStyle(this.$el).width;

            if (this.settings.resizable) {
                this.$container.style.resize = 'both';
            } else {
                this.$container.style.resize = 'none';
            }

            $h.style.whiteSpace = 'pre';
        }

        $h.style.fontFamily = window.getComputedStyle(this.$el).fontFamily;
        $h.style.fontSize = window.getComputedStyle(this.$el).fontSize;
        $h.style.fontStyle = window.getComputedStyle(this.$el).fontStyle;
        $h.style.fontWeight = window.getComputedStyle(this.$el).fontWeight;
        $h.style.fontVariant = window.getComputedStyle(this.$el).fontVariant;
        $h.style.wordWrap = 'break-word';

        if (this.settings.debug) {
            $h.style['outline'] = '1px solid #FFD700';
        }
    }

    applyResizable(): void {
        const $main = this.$main;
        const $el = this.$el;
        const $container = this.$container;

        let startX: number, startY: number, startWidth: number, startHeight: number;

        const startDrag = (e: MouseEvent): void => {
            startX = e.clientX;
            startY = e.clientY;
            startWidth = parseInt(document.defaultView!.getComputedStyle($container).width, 10);
            startHeight = parseInt(document.defaultView!.getComputedStyle($container).height, 10);

            document.documentElement.addEventListener('mousemove', doDrag, false);
            document.documentElement.addEventListener('mouseup', stopDrag, false);
            e.preventDefault();
        };

        const doDrag = (e: MouseEvent): void => {
            $container.style.width = startWidth + e.clientX - startX + 'px';
            $container.style.height = startHeight + e.clientY - startY + 'px';
            e.preventDefault();
        };

        const stopDrag = (): void => {
            document.documentElement.removeEventListener('mousemove', doDrag, false);
            document.documentElement.removeEventListener('mouseup', stopDrag, false);
        };

        const $handle = document.createElement('div');
        $handle.style.cursor = 'se-resize';
        $handle.style.position = 'absolute';
        $handle.style.bottom = '0';
        $handle.style.right = '0';
        $handle.style.width = '10px';
        $handle.style.height = '10px';

        $handle.addEventListener('mousedown', startDrag, false);

        $main.appendChild($handle);
    }

    updateSizePosition(): void {
        const $h = this.$highlighter;
        const $c = this.$container;

        let width: number, height: number;

        if (this.isInput) {
            width = 99999;
            height = $c.offsetHeight;
        } else {
            width = $c.offsetWidth;
            height = $c.offsetHeight - this.scrollbarWidth;
        }
        
        $h.style.width = width + 'px';
        $h.style.height = height + 'px';
        $h.style.top = - $c.scrollTop + 'px';
        $h.style.left = - this.$el.scrollLeft + 'px';
    }

    bindEvents(): void {
        if (this.isInput) {
            this.$el.addEventListener('keydown', this.throttle(this.highlight, 50, this), false);
            this.$el.addEventListener('input', this.throttle(this.highlight, 50, this), false);
            this.$el.addEventListener('keyup', this.throttle(this.highlight, 50, this), false);
            this.$el.addEventListener('keypress', this.throttle(this.highlight, 50, this), false);
            this.$el.addEventListener('blur', () => {
                setTimeout(() => {
                    this.$el.setSelectionRange(0, 0);
                    this.updateSizePosition();
                }, 50);
            });
            this.$el.addEventListener('focus', () => {
                setTimeout(() => {
                    this.updateSizePosition();
                }, 50);
            });
        } else {
            this.$el.addEventListener('scroll', this.throttle(this.highlight, 50, this), false);
            this.$el.addEventListener('resize', this.throttle(this.highlight, 50, this), false);
        }
    }

    unbindEvents(): void {
        if (this.isInput) {
            this.$el.removeEventListener('input', this.throttle(this.highlight, 50, this), false);
        } else {
            this.$el.removeEventListener('scroll', this.throttle(this.highlight, 50, this), false);
            this.$el.removeEventListener('resize', this.throttle(this.highlight, 50, this), false);
        }
    }

    bindDocumentEvents(): void {
        window.addEventListener('resize', this.throttle(this.updateSizePosition, 50, this), false);
    }

    throttle(callback: () => void, delay: number, context: any): () => void {
        return Utilities.throttle(callback, delay, context);
    }
}
